import { cn } from '@april9au/react-common/utils';

import type { CtaLink } from './cta-link';
import { CtaLinkButton } from './cta-link';

export type CtaContactLink = {
  /** e.g. "Mobile", "Office", "Email" — shown as a small caption above the value. Omit for a value that's self-explanatory (a website URL). */
  label?: string;
  value: string;
  href?: string;
};

/**
 * Closing call-to-action banner. `primaryCta` renders filled gold,
 * `secondaryCta` (typically a "Call Lucio" tel: link) renders as a hairline
 * outline button beside/below it — never two filled buttons, which would
 * halve each other, but an outline pairs fine with one filled action.
 * Reusable at the end of the homepage, listing, or service pages.
 */
export function CtaBanner({
  id,
  eyebrow,
  heading,
  description,
  primaryCta,
  secondaryCta,
  contactName,
  contactTitle,
  contactLinks,
  className,
}: {
  id?: string;
  eyebrow?: string;
  heading: string;
  description?: string;
  primaryCta?: CtaLink;
  secondaryCta?: CtaLink;
  /** Person's name — paired with `contactTitle` the same way a job title reads under a name, so neither needs a label. */
  contactName?: string;
  contactTitle?: string;
  contactLinks?: Array<CtaContactLink>;
  className?: string;
}) {
  return (
    <section id={id} className={cn('section', className)}>
      <div className="container">
        <div className="cta-block grid items-center gap-10 lg:grid-cols-[1fr_340px]">
          <div>
            {eyebrow ? <p className="text-eyebrow mb-6">{eyebrow}</p> : null}
            <h2 className="text-h2">{heading}</h2>

            {description ? <p className="text-lead mt-4 max-w-[68ch]">{description}</p> : null}

            {contactName || contactTitle ? (
              <div className="mt-7">
                {contactName ? <p className="text-foreground font-semibold">{contactName}</p> : null}
                {contactTitle ? <p className="text-caption mt-1">{contactTitle}</p> : null}
              </div>
            ) : null}

            {contactLinks?.length ? (
              <div
                className={cn(
                  // 2 columns, not 4 — "lucio@meetrealestate.com.au" needs more
                  // room than a 4-up grid gives it in this banner's ~2/3-width
                  // left column, and breaks mid-word otherwise.
                  'grid grid-cols-2 gap-x-8 gap-y-5 border-t border-[var(--color-border)] pt-6',
                  contactName || contactTitle ? 'mt-6' : 'mt-7',
                )}
              >
                {contactLinks.map((link) => (
                  <div key={link.value} className="min-w-0">
                    {link.label ? <p className="text-caption mb-1.5">{link.label}</p> : null}
                    {link.href ? (
                      <a href={link.href} className="contact-detail break-words">
                        {link.value}
                      </a>
                    ) : (
                      <span className="contact-detail break-words">{link.value}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {primaryCta || secondaryCta ? (
            <div className="flex flex-col gap-4">
              {primaryCta ? <CtaLinkButton cta={primaryCta} size="lg" className="w-full" /> : null}
              {secondaryCta ? <CtaLinkButton cta={secondaryCta} size="lg" variant="secondary" className="w-full" /> : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
